// One campaign (UI-2): where it is, country by country, and what can be done
// about it. Every move is a dialog that says what it does; the ones the API
// records a reason for ask for one.
//
//   header      — state, and the moves the state allows
//   numbers     — recipients, sent, delivered, bounces, complaints; opens,
//                 clicks and unsubscribes from the reports once there are any
//   countries   — each country's progress (sent / recipients), its local send
//                 time, and its own pause, resume, stop or restart
//   A/B         — the arms, the winners, and the manual decision while testing
//   explain     — why one address did or did not get it (rule 6)
//   over time   — a curve, only when the event warehouse returns a series

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { routeId } from "@/lib/utils";
import { toast } from "sonner";
import { EllipsisIcon, PauseIcon, PencilIcon, PlayIcon, RefreshCwIcon, SearchIcon, SquareIcon } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  cancelCampaign,
  decideGroupAb,
  explainAddress,
  fetchCampaignReport,
  giveUpCampaignGroup,
  pauseCampaign,
  restartGroupCountry,
  resumeCampaign,
} from "@/lib/api";
import type {
  AbDecision,
  CampaignGroupSummary,
  CampaignReport,
  Explanation,
  GroupCountryDetail,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { Curve } from "@/components/emc/charts/Curve";
import { LimitBar } from "@/components/emc/charts/LimitBar";
import { DataTable } from "@/components/emc/DataTable";
import { DetailList } from "@/components/emc/DetailList";
import { EnumText } from "@/components/emc/EnumText";
import { PageHeader } from "@/components/emc/PageHeader";
import { RowActions } from "@/components/emc/RowActions";
import type { RowAction } from "@/components/emc/RowActions";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import type { Tone } from "@/components/emc/status";
import { campaignGroupShown, labelOf, statusLook } from "@/components/emc/status";
import { count, minute, share, shareOf } from "@/components/emc/format";
import { automaticText } from "@/pages/reports/model";
import { usePageCrumb } from "@/components/emc/shell/crumb";
import { GroupActionDialog } from "@/pages/campaigns/CampaignList";
import type { Pending } from "@/pages/campaigns/CampaignList";
import { pausedOf, sendingOf } from "@/pages/campaigns/actions";
import { useGroup, useHaltStatus, useSendingIdentities } from "@/pages/campaigns/data";
import {
  COMPOSING,
  COUNTRY_LIVE,
  MOVING,
  assembled,
  audienceText,
  engagementOf,
  messageOf,
  showLocal,
  startsLine,
  startsText,
  totalsOf,
} from "@/pages/campaigns/model";
import { Country } from "@/components/emc/Flag";
import { atViewer, countryClock } from "@/components/emc/time";

const REFRESH_MS = 10_000;
const REPORT_REFRESH_MS = 60_000;

export default function CampaignDetail() {
  const params = useParams();
  const groupId = routeId(params.id);
  if (groupId === null) return <Navigate to="/kampanyok" replace />;
  return <Detail key={groupId} groupId={groupId} />;
}

type CountryMove =
  | { kind: "pause"; row: GroupCountryDetail }
  | { kind: "resume"; row: GroupCountryDetail }
  | { kind: "cancel"; row: GroupCountryDetail }
  | { kind: "restart"; row: GroupCountryDetail };

function Detail({ groupId }: { groupId: number }) {
  const { user } = useAuth();
  const group = useGroup(groupId);
  const { identityOf } = useSendingIdentities();
  const halt = useHaltStatus();
  const [reports, setReports] = useState<Map<number, CampaignReport>>(new Map());
  const [reportError, setReportError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [countryMove, setCountryMove] = useState<CountryMove | null>(null);
  const [givingUp, setGivingUp] = useState(false);

  const { summary } = group;
  usePageCrumb(summary?.name ?? null);

  const moving = summary !== null && MOVING.has(summary.state);
  const reload = group.reload;
  useEffect(() => {
    if (!moving) return undefined;
    const timer = window.setInterval(() => void reload(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [moving, reload]);

  // The report of every country that has started, from the reports endpoint.
  const started = useMemo(
    () => (summary?.countries ?? []).filter((row) => row.started_at !== null).map((row) => row.campaign_id),
    [summary],
  );
  const startedKey = started.join(",");
  const loadReports = useCallback(async () => {
    if (started.length === 0) return;
    try {
      const answers = await Promise.all(started.map((id) => fetchCampaignReport(id)));
      setReports(new Map(answers.map((report) => [report.campaign_id, report] as const)));
      setReportError(null);
    } catch (raised: unknown) {
      setReportError(messageOf(raised));
    }
    // `startedKey` is the content of `started`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedKey]);
  useEffect(() => {
    void loadReports();
    if (!moving) return undefined;
    const timer = window.setInterval(() => void loadReports(), REPORT_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [loadReports, moving]);

  if (group.error !== null && summary === null) {
    return (
      <>
        <PageHeader back={{ to: "/kampanyok", label: "Kampányok" }} title="Kampány" />
        <ErrorState message={group.error} onRetry={() => void group.reload()} />
      </>
    );
  }
  if (summary === null || group.detail === null) {
    return (
      <>
        <PageHeader back={{ to: "/kampanyok", label: "Kampányok" }} title="Kampány" />
        <LoadingState lines={6} />
      </>
    );
  }

  const composing = COMPOSING.has(summary.state);
  const sending = sendingOf(summary.countries);
  const paused = pausedOf(summary.countries);
  const totals = totalsOf(summary.countries);
  const engagement = engagementOf(summary.countries, reports);
  const isAdmin = user?.role === "admin";
  const canGiveUp = isAdmin && ["scheduled", "ab_testing", "ab_deciding", "sending"].includes(summary.state);
  const zoneOf = (country: string) => identityOf(country)?.timezone ?? undefined;
  const starts = startsLine(
    summary.countries.map((country) => ({ country: country.country, at: country.send_at })),
    zoneOf,
  );

  const actions = (
    <>
      {started.length > 0 && <Button variant="outline" asChild><Link to={`/riportok/csoport/${groupId}`}>Riport</Link></Button>}
      {composing && (
        <Button asChild>
          <Link to={`/kampanyok/${groupId}/szerkesztes`}>
            <PencilIcon />
            Szerkesztés folytatása
          </Link>
        </Button>
      )}
      {sending.length > 0 && (
        <Button variant="outline" type="button" onClick={() => setPending({ kind: "pause", row: summary })}>
          <PauseIcon />
          Szüneteltetés
        </Button>
      )}
      {paused.length > 0 && (
        <Button type="button" onClick={() => setPending({ kind: "resume", row: summary })}>
          <PlayIcon />
          Folytatás
        </Button>
      )}
      {(composing || moving) && (
        <Button variant="outline-danger" type="button" onClick={() => setPending({ kind: "cancel", row: summary })}>
          <SquareIcon />
          {composing ? "Lemondás" : "Leállítás"}
        </Button>
      )}
      {canGiveUp && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="További műveletek">
              <EllipsisIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem variant="destructive" onSelect={() => setGivingUp(true)}>
              Feladás (admin)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );

  const bounceShare = totals.sent > 0 ? totals.bounced / totals.sent : 0;
  const complaintShare = totals.sent > 0 ? totals.complained / totals.sent : 0;
  // Rule 4's lines, per pool and day in the warmup; here a campaign's own
  // reading of the same thresholds.
  const bounceTone: Tone = bounceShare > 0.03 ? "danger" : bounceShare > 0.02 ? "warn" : "muted";
  const complaintTone: Tone = complaintShare > 0.002 ? "danger" : complaintShare > 0.001 ? "warn" : "muted";

  return (
    <>
      <PageHeader
        back={{ to: "/kampanyok", label: "Kampányok" }}
        title={summary.name}
        meta={<StatusPill domain="campaignGroup" state={campaignGroupShown(summary)} title={summary.state} />}
        description={
          <>
            {labelOf("campaignMode", summary.mode)} · {summary.countries.length} ország
            {starts !== null
              ? ` · indulás ${startsText(starts)}`
              : summary.scheduled_local
                ? ` · indulás ${showLocal(summary.scheduled_local)} helyi idő szerint`
                : ""}{" "}
            ·
            létrehozta {summary.created_by}
          </>
        }
        actions={actions}
      />

      <div className="flex flex-col gap-6">
        {halt.status?.halted && moving && (
          <Callout tone="warn">
            A kiküldés globálisan áll ({halt.status.halted_by}
            {halt.status.note ? `: ${halt.status.note}` : ""}). Ez a kampány a feloldásig vár; utána
            onnan folytatódik, ahol megállt.
          </Callout>
        )}
        {composing && (
          <Callout>
            Ez a kampány még {statusLook("campaignGroup", summary.state).label}: nem ment ki semmi. A
            szerkesztés és az indítás a{" "}
            <Link to={`/kampanyok/${groupId}/szerkesztes?lepes=attekintes`}>szerkesztőben</Link> folytatható.
          </Callout>
        )}

        <StatGrid variant="strip" className="grid-cols-[repeat(auto-fit,minmax(8rem,1fr))]">
          <StatTile
            label="Címzett"
            value={assembled(summary.countries) ? count(totals.recipients) : "—"}
            comparison={assembled(summary.countries) ? "a kizárások után" : "összeállításkor számolódik"}
          />
          <StatTile
            label="Elküldve"
            value={count(totals.sent)}
            comparison={shareOf(totals.sent, totals.recipients, "a címzettek")}
          />
          <StatTile
            label="Kézbesítve"
            value={count(totals.delivered)}
            comparison={shareOf(totals.delivered, totals.sent, "az elküldöttek")}
          />
          <StatTile
            label="Visszapattant"
            value={count(totals.bounced)}
            comparison={shareOf(totals.bounced, totals.sent, "az elküldöttek")}
            tone={bounceTone}
          />
          <StatTile
            label="Panasz"
            value={count(totals.complained)}
            comparison={shareOf(totals.complained, totals.sent, "az elküldöttek")}
            tone={complaintTone}
          />
          <StatTile
            label="Egyedi nyitás"
            value={engagement?.opens === null || engagement === null ? "—" : count(engagement.opens)}
            comparison={
              engagement === null
                ? "még nincs riport"
                : engagement.opens === null
                  ? "nem minden országra van szám"
                  : (
                      <>
                        {shareOf(engagement.opens, engagement.delivered, "a kézbesítettek") ?? "még nincs kézbesített levél"}
                        {automaticText(engagement.opensMachine, engagement.opens) && (
                          <span className="block">{automaticText(engagement.opensMachine, engagement.opens)}</span>
                        )}
                      </>
                    )
            }
          />
          <StatTile
            label="Egyedi kattintás"
            value={engagement?.clicks === null || engagement === null ? "—" : count(engagement.clicks)}
            comparison={
              engagement === null
                ? "még nincs riport"
                : engagement.clicks === null
                  ? "nem minden országra van szám"
                  : shareOf(engagement.clicks, engagement.sent, "az elküldöttek")
            }
          />
          <StatTile
            label="Leiratkozás"
            value={engagement?.unsubscribed === null || engagement === null ? "—" : count(engagement.unsubscribed)}
            comparison={
              engagement === null ? "még nincs riport" : shareOf(engagement.unsubscribed, engagement.sent, "az elküldöttek")
            }
          />
        </StatGrid>
        {reportError !== null && (
          <p className="-mt-3 text-label text-muted-foreground">
            A riport most nem válaszolt ({reportError}) — a nyitás, kattintás és leiratkozás ezért „—”.
          </p>
        )}

        <CountriesCard
          rows={summary.countries}
          zoneOf={zoneOf}
          groupLive={composing || moving}
          onMove={setCountryMove}
        />

        {summary.ab !== null && <AbCard groupId={groupId} summary={summary} onDecided={() => void group.reload()} />}

        <ExplainCard rows={summary.countries} />

        <OverTimeCard reports={reports} onRefresh={() => void loadReports()} started={started.length > 0} />
      </div>

      <GroupActionDialog pending={pending} onClose={() => setPending(null)} onDone={() => void group.reload()} />

      <CountryMoveDialog move={countryMove} groupId={groupId} onClose={() => setCountryMove(null)} onDone={() => void group.reload()} />

      <ActionDialog
        open={givingUp}
        onOpenChange={setGivingUp}
        tone="danger"
        title="A kampány feladása"
        description="A kampány „hiba” állapotba kerül: ez annak a rögzítése, hogy ez a kiküldés nem fog megtörténni. Admin döntés; a már kiküldött levelek nem érintettek."
        reason={{ label: "Miért adjuk fel?", placeholder: "pl. a RO ország harmadszor is elbukott" }}
        confirmLabel="Feladás"
        onConfirm={async (reason) => {
          await giveUpCampaignGroup(groupId, reason);
          toast.success("A kampány feladva", { description: summary.name });
          void group.reload();
        }}
      />
    </>
  );
}

// --- the countries --------------------------------------------------------------------

function CountriesCard({
  rows,
  zoneOf,
  groupLive,
  onMove,
}: {
  rows: GroupCountryDetail[];
  zoneOf: (country: string) => string | undefined;
  groupLive: boolean;
  onMove: (move: CountryMove) => void;
}) {
  // Six columns that fit the 1104 px a 1440 px screen leaves inside the card:
  // the audience rides under the country, the provider's two signals share a
  // cell, a failure or an unknown shows under the bar only when there is one,
  // and the stop — the rare, final move — sits in the row's menu.
  const columns: ColumnDef<GroupCountryDetail>[] = [
    {
      id: "country",
      header: "Ország",
      cell: ({ row }) => (
        <span className="flex max-w-56 flex-col">
          <Country code={row.original.country} />
          <span className="text-label text-muted-foreground">
            {audienceText(row.original.audience, row.original.frequency_cap_days)}
          </span>
        </span>
      ),
      meta: { wrap: true },
    },
    {
      id: "state",
      header: "Állapot",
      cell: ({ row }) => <StatusPill domain="campaign" state={row.original.state} />,
    },
    {
      id: "progress",
      header: "Haladás",
      cell: ({ row }) => {
        const country = row.original;
        if (country.recipient_total <= 0) {
          return <span className="text-muted-foreground">még nincs összeállítva</span>;
        }
        const troubled = country.failed_count + country.unknown_count;
        return (
          <span className="flex min-w-44 flex-col gap-1">
            <LimitBar
              used={country.sent_count}
              limit={country.recipient_total}
              label="elküldve"
              tone={
                country.state === "failed" || country.state === "cancelled"
                  ? "danger"
                  : country.state === "done"
                    ? "good"
                    : country.state === "paused"
                      ? "warn"
                      : "running"
              }
            />
            {troubled > 0 && (
              <span className="text-label text-warn">
                hiba {count(country.failed_count)} · bizonytalan {count(country.unknown_count)}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: "when",
      header: "Indulás",
      cell: ({ row }) => {
        const there = countryClock(row.original.send_at, zoneOf(row.original.country));
        return (
          <span className="flex flex-col">
            <span className="data-mono">{atViewer(row.original.send_at)}</span>
            {there !== null && <span className="data-mono text-label text-muted-foreground">({there})</span>}
          </span>
        );
      },
    },
    {
      id: "signals",
      header: "Visszajelzés",
      cell: ({ row }) => (
        <span className="flex flex-col items-end">
          <span>
            {count(row.original.bounced_count)} visszapattant{" "}
            <span className="text-muted-foreground">
              ({share(row.original.bounced_count, row.original.sent_count) ?? "—"})
            </span>
          </span>
          <span>
            {count(row.original.complained_count)} panasz{" "}
            <span className="text-muted-foreground">
              ({share(row.original.complained_count, row.original.sent_count) ?? "—"})
            </span>
          </span>
        </span>
      ),
      meta: { align: "right" },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Műveletek</span>,
      cell: ({ row }) => {
        const country = row.original;
        const more: RowAction[] =
          COUNTRY_LIVE.has(country.state) && groupLive
            ? [{ label: `${country.country} leállítása`, danger: true, onSelect: () => onMove({ kind: "cancel", row: country }) }]
            : [];
        return (
          <span className="flex items-center justify-end gap-1">
            {country.state === "sending" && (
              <Button variant="outline" size="sm" type="button" onClick={() => onMove({ kind: "pause", row: country })}>
                Szünet
              </Button>
            )}
            {country.state === "paused" && (
              <Button size="sm" type="button" onClick={() => onMove({ kind: "resume", row: country })}>
                Folytatás
              </Button>
            )}
            {country.state === "failed" && groupLive && (
              <Button size="sm" type="button" onClick={() => onMove({ kind: "restart", row: country })}>
                Újraindítás
              </Button>
            )}
            <RowActions actions={more} label={`${country.country}: további műveletek`} />
          </span>
        );
      },
      meta: { align: "right" },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Országonként</CardTitle>
        <CardDescription>
          Minden ország a saját kampánya: a saját állapotával, ütemével és warmup-keretével. Egy
          ország hibája nem állítja meg a többit, és külön újraindítható.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          label="Országonként"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          columns={columns}
          data={rows}
          getRowId={(row) => row.country}
          empty={<p className="px-4 py-6 text-center text-muted-foreground">Még nincs ország.</p>}
        />
      </CardContent>
    </Card>
  );
}

function CountryMoveDialog({
  move,
  groupId,
  onClose,
  onDone,
}: {
  move: CountryMove | null;
  groupId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const country = move?.row.country ?? "";
  const close = (open: boolean) => !open && onClose();
  return (
    <>
      <ActionDialog
        open={move?.kind === "pause"}
        onOpenChange={close}
        title={`A kiküldés szüneteltetése: ${country}`}
        description="Ebben az országban megáll a kiküldés; a hátralévő címzettek a folytatásig várnak. A többi ország megy tovább."
        confirmLabel="Szüneteltetés"
        onConfirm={async () => {
          if (!move) return;
          await pauseCampaign(move.row.campaign_id);
          toast.success(`A kiküldés szünetel: ${country}`);
          onDone();
        }}
      />
      <ActionDialog
        open={move?.kind === "resume"}
        onOpenChange={close}
        title={`A kiküldés folytatása: ${country}`}
        description="Ebben az országban újraindul a kiküldés, a warmup-keret és a küldési ablak szerint."
        confirmLabel="Folytatás"
        onConfirm={async () => {
          if (!move) return;
          await resumeCampaign(move.row.campaign_id);
          toast.success(`A kiküldés folytatódik: ${country}`);
          onDone();
        }}
      />
      <ActionDialog
        open={move?.kind === "cancel"}
        onOpenChange={close}
        tone="danger"
        title={`Az ország leállítása: ${country}`}
        description="Ebben az országban a hátralévő címzettek nem kapnak levelet. A már kiküldött levél nem vonható vissza, és ez a leállítás sem. A többi ország megy tovább."
        reason={{ label: "Miért?", placeholder: "pl. rossz nyelvű levél ment ki" }}
        confirmLabel="Leállítás"
        onConfirm={async (reason) => {
          if (!move) return;
          await cancelCampaign(move.row.campaign_id, reason);
          toast.success(`Az ország leállt: ${country}`);
          onDone();
        }}
      />
      <ActionDialog
        open={move?.kind === "restart"}
        onOpenChange={close}
        title={`Újraindítás: ${country}`}
        description="A hibás ország új kampányként indul ugyanazzal a közönséggel és levéllel; a hibás marad a naplóban. Aki már megkapta a levelet, nem kapja meg újra — a kizárásokat és a gyakorisági korlátot az új összeállítás is ellenőrzi."
        confirmLabel="Újraindítás"
        onConfirm={async () => {
          if (!move) return;
          await restartGroupCountry(groupId, move.row.country);
          toast.success(`Az újraindítás elindult: ${country}`);
          onDone();
        }}
      />
    </>
  );
}

// --- A/B ---------------------------------------------------------------------------------

function AbCard({
  groupId,
  summary,
  onDecided,
}: {
  groupId: number;
  summary: CampaignGroupSummary;
  onDecided: () => void;
}) {
  const [deciding, setDeciding] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<AbDecision[] | null>(null);
  const testing = summary.state === "ab_testing";
  const winners = summary.countries.filter((row) => row.ab_winner !== null);
  const metric = typeof summary.ab?.metric === "string" ? summary.ab.metric : "open";

  return (
    <Card>
      <CardHeader>
        <CardTitle>A/B teszt</CardTitle>
        <CardDescription>
          A variánsok a közönség egy részén futnak; a maradék a győztessel megy ki. A győztes a
          magasabb {labelOf("abMetric", metric)}i arány
          {summary.decide_at ? `, döntés ${minute(summary.decide_at)}-kor (a te idődben)` : ""}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <DataTable
          label="Variánsok"
          variant="plain"
          className="-mx-5.5 border-t border-separator"
          data={summary.ab_arms}
          getRowId={(arm) => arm.key}
          columns={[
            { id: "key", header: "Variáns", cell: ({ row }) => row.original.key, meta: { mono: true } },
            { id: "subject", header: "Tárgy", cell: ({ row }) => row.original.subject || "—", meta: { wrap: true } },
            {
              id: "share",
              header: "Hányad",
              cell: ({ row }) => `${(row.original.share_permille / 10).toLocaleString("hu-HU")}%`,
              meta: { align: "right" },
            },
            {
              id: "decide",
              header: () => <span className="sr-only">Döntés</span>,
              cell: ({ row }) =>
                testing ? (
                  <Button variant="outline" size="sm" type="button" onClick={() => setDeciding(row.original.key)}>
                    {row.original.key} nyer
                  </Button>
                ) : null,
              meta: { align: "right" },
            },
          ]}
        />
        {winners.length > 0 && (
          <p className="text-muted-foreground">
            Győztes:{" "}
            {winners.map((row, index) => (
              <span key={row.country}>
                {index > 0 && ", "}
                <span className="data-mono text-ink">{row.country}</span> → {row.ab_winner}
              </span>
            ))}
          </p>
        )}
        {decisions !== null && (
          <DataTable
            label="A döntés országonként"
            variant="plain"
            className="-mx-5.5 border-t border-separator"
            data={decisions}
            getRowId={(row) => String(row.campaign_id)}
            columns={[
              { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
              { id: "winner", header: "Győztes", cell: ({ row }) => row.original.winner, meta: { mono: true } },
              {
                id: "results",
                header: "Arányok",
                cell: ({ row }) =>
                  row.original.results
                    .map((result) => `${result.key}: ${(result.rate * 100).toLocaleString("hu-HU", { maximumFractionDigits: 2 })}% (${count(result.engaged)}/${count(result.sent)})`)
                    .join(" · "),
                meta: { wrap: true },
              },
              {
                id: "released",
                header: "Elengedve",
                cell: ({ row }) => count(row.original.released),
                meta: { align: "right" },
              },
            ]}
          />
        )}
      </CardContent>
      <ActionDialog
        open={deciding !== null}
        onOpenChange={(open) => !open && setDeciding(null)}
        title={`Győztes kihirdetése: ${deciding ?? ""}`}
        description={`A teszt most véget ér: minden országban a(z) ${deciding ?? ""} variáns tárgyával megy ki a maradék. Ez nem vonható vissza.`}
        confirmLabel={`A(z) ${deciding ?? ""} variáns győz`}
        onConfirm={async () => {
          if (deciding === null) return;
          const answer = await decideGroupAb(groupId, deciding);
          setDecisions(answer);
          toast.success(`A győztes kihirdetve: ${deciding}`, {
            description: `${answer.length} ország maradéka a győztessel megy ki.`,
          });
          onDecided();
        }}
      />
    </Card>
  );
}

// --- one address, explained --------------------------------------------------------------

function explainSentence(answer: Explanation): string {
  const detail = answer.detail;
  const text = (key: string) => (typeof detail[key] === "string" ? (detail[key] as string) : null);
  // The country was parallel when the audience was frozen (0045): no handover
  // filter stood between the contact and the letter.
  const parallel = text("handover") === "parallel" ? ` A Klaviyo is küld nekik — nincs átadás-szűrés.` : "";
  // The audience segments that let the contact in (UI-11), and the segments
  // of an audience it is in none of.
  const names = (key: string) =>
    Array.isArray(detail[key])
      ? (detail[key] as { segment_id?: number; segment?: string | null }[])
          .map((row) => `„${row.segment ?? `#${String(row.segment_id ?? "?")}`}”`)
          .join(", ")
      : "";
  const through = names("audience_segments") ? ` A közönségbe ezen keresztül: ${names("audience_segments")}.` : "";
  const held = detail.suppression as { reason?: string; scope?: string } | undefined;
  const besides = held
    ? ` Emellett kizárva: ${labelOf("suppressionReason", held.reason ?? "")}, ${held.scope === "global" ? "minden országban" : `csak ${held.scope ?? "?"} országban`}.`
    : "";
  switch (answer.verdict) {
    case "recipient": {
      const state = text("state");
      const sent = text("sent_at");
      return `A címzettek között van. A levele: ${state ? statusLook("recipient", state).label : "—"}${sent ? `, ${minute(sent)}` : ""}.${text("error") ? ` Hiba: ${text("error")}.` : ""}${through}${parallel}`;
    }
    case "excluded_by_hygiene":
      return `Összeállításkor a listahigiénia kihagyta${text("rule") ? ` („${text("rule")}” szabály)` : ""}.`;
    case "suppressed": {
      const scope = text("scope");
      return `Kizárva: ${labelOf("suppressionReason", text("reason") ?? "")}, ${labelOf("suppressionSource", text("source") ?? "")} alapján, ${scope === "global" ? "minden országban" : `csak ${scope ?? "?"} országban`}. Első jel: ${minute(text("first_signal_at"))}.`;
    }
    case "absent":
      return "Nincs jelen: a profilja az ország Klaviyo-fiókjának legutóbbi teljes olvasásakor már nem volt meg, ezért nem kap levelet.";
    case "not_subscribed":
      return `Nincs feliratkozva (hozzájárulás: ${text("consent_status") ?? "—"}), ezért nem kap marketinglevelet.`;
    case "frequency_capped": {
      const counted = Array.isArray(detail.counted) ? detail.counted.length : null;
      return `Pihentetve: a gyakorisági korlát miatt kimarad${counted !== null ? ` (${counted} levelet kapott a számolt időszakban)` : ""}.`;
    }
    case "excluded_by_segment":
      return `A(z) „${text("segment") ?? `#${String(detail.segment_id ?? "?")}`}” kizáró szegmensben van.`;
    case "group_duplicate": {
      // Owner, 2026-10-27: one letter per address per campaign group.
      const given = Array.isArray(detail.given)
        ? (detail.given as { country?: string; campaign_id?: number }[])
            .map((row) => `${row.country ?? "?"} (#${String(row.campaign_id ?? "?")})`)
            .join(", ")
        : "";
      return `Másik országból kapja: a kampánycsoport egy címre egy levelet küld${given ? `, ezt a címet már ${given} kapta meg` : ""}.`;
    }
    case "not_in_audience":
      // Only a segment leaves anybody of the country out: the whole country
      // has no such condition (UI-10).
      if (text("audience") === "segment") {
        return `Nincs a kampány közönségében: nem tagja a(z) „${text("segment") ?? `#${String(detail.segment_id ?? "?")}`}” szegmensnek.${besides}`;
      }
      if (text("audience") === "segments") {
        return `Nincs a kampány közönségében: egyik szegmensnek sem tagja (${names("segments")}).${besides}`;
      }
      return `Nincs a kampány közönségében.${besides}`;
    case "not_in_country": {
      const elsewhere = Array.isArray(detail.countries) ? (detail.countries as string[]) : [];
      return elsewhere.length > 0
        ? `Ebben az országban nincs ilyen kontakt. Ezekben szerepel: ${elsewhere.join(", ")} — ott a saját ország kampánya dönt.`
        : "Ilyen címet egyik országban sem ismerünk.";
    }
    case "handover": {
      // The Klaviyo handover (0028): a row the worker took out before the
      // send carries `state`; one left out at assembly carries the rule.
      const n = typeof detail.n === "number" ? detail.n : null;
      const wave = typeof detail.migration_wave === "number" ? detail.migration_wave : null;
      const why =
        text("reason") === "no_wave"
          ? "nincs hulláma"
          : text("reason") === "wave_above_n"
            ? `a hulláma (${wave ?? "?"}) nagyobb, mint N`
            : text("reason") === "not_synced"
              ? "a hullámát a Klaviyo még nem kapta meg"
              : null;
      return `A migráció alatt a Klaviyóé${n !== null ? ` (N = ${n})` : ""}: ${why ?? "hullám > N vagy nincs szinkronizálva"} — neki a Klaviyo küld, tőlünk nem kap levelet.`;
    }
    case "eligible":
      return `Semmi nem zárja ki, de még nincs a címzettek között: az ország még nincs összeállítva, vagy az összeállítás óta került be.${through}${parallel}`;
    default:
      return "A válasz részletei lent.";
  }
}

function ExplainCard({ rows }: { rows: GroupCountryDetail[] }) {
  const [country, setCountry] = useState(rows[0]?.country ?? "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<Explanation | null>(null);
  const chosen = rows.find((row) => row.country === country) ?? rows[0];

  async function ask() {
    if (chosen === undefined || email.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      setAnswer(await explainAddress(chosen.campaign_id, email.trim()));
    } catch (raised: unknown) {
      setAnswer(null);
      setError(messageOf(raised));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Miért kapta meg — vagy miért nem?</CardTitle>
        <CardDescription>
          Egy cím és egy ország: a válasz ugyanazzal a kóddal készül, amivel a kiküldés a
          címzetteket választja.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void ask();
          }}
        >
          <NativeSelect aria-label="Ország" value={chosen?.country ?? ""} onChange={(event) => setCountry(event.target.value)}>
            {rows.map((row) => (
              <option key={row.country} value={row.country}>
                {row.country}
              </option>
            ))}
          </NativeSelect>
          <Input
            aria-label="Email-cím"
            className="w-80"
            placeholder="cím@example.hu"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" variant="outline" disabled={busy || email.trim() === "" || chosen === undefined}>
            <SearchIcon />
            {busy ? "Keresés…" : "Megnézem"}
          </Button>
        </form>
        {error !== null && <ErrorState message={error} />}
        {answer !== null && (
          <div className="flex flex-col gap-3 rounded-card border border-line p-4" data-slot="explain-answer">
            <p className="flex flex-wrap items-center gap-2">
              <span className="data-mono">{answer.email}</span>
              <StatusPill domain="explainVerdict" state={answer.verdict} />
            </p>
            <p className="text-ink">{explainSentence(answer)}</p>
            {answer.verdict === "suppressed" && typeof answer.detail.reason === "string" && (
              <p className="text-muted-foreground">
                Ok: <EnumText domain="suppressionReason" value={answer.detail.reason} />
              </p>
            )}
            <details>
              <summary className="cursor-pointer text-label text-muted-foreground">Részletek (a napló nyelvén)</summary>
              <DetailList
                className="mt-2"
                items={Object.entries(answer.detail).map(([key, value]) => ({
                  label: key,
                  value: typeof value === "object" && value !== null ? JSON.stringify(value) : String(value ?? "—"),
                  mono: true,
                }))}
              />
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- over time ------------------------------------------------------------------------------

/** An hour bucket on the reader's clock, as short as it can be and still
 * unique: "13:00" when the series is one day, "29. 13:00" (day, hour) when
 * it spans several — a long label is clipped at the chart's left edge. */
function hourLabels(hours: string[]): string[] {
  const dates = hours.map((value) => new Date(value));
  const day = (date: Date) => date.toLocaleDateString("hu-HU");
  const oneDay = new Set(dates.map(day)).size <= 1;
  return dates.map((date) => {
    const time = date.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" });
    return oneDay ? time : `${date.getDate()}. ${time}`;
  });
}

function OverTimeCard({
  reports,
  onRefresh,
  started,
}: {
  reports: Map<number, CampaignReport>;
  onRefresh: () => void;
  started: boolean;
}) {
  // Opens per hour, summed over the countries — only if the warehouse gave a
  // series for at least one of them. Unique counts add up across countries
  // (a contact is one country's), not across hours, so this is per hour.
  const points = useMemo(() => {
    const byHour = new Map<string, number>();
    let any = false;
    for (const report of reports.values()) {
      if (report.timeline === null) continue;
      any = true;
      for (const bucket of report.timeline) {
        if (bucket.kind !== "open") continue;
        byHour.set(bucket.hour, (byHour.get(bucket.hour) ?? 0) + bucket.uniq);
      }
    }
    if (!any) return null;
    const sorted = [...byHour.entries()].sort(([a], [b]) => (a < b ? -1 : 1));
    const labels = hourLabels(sorted.map(([hour]) => hour));
    return sorted.map(([, value], index) => ({ label: labels[index] ?? "", actual: value }));
  }, [reports]);

  if (!started) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Nyitások időben</CardTitle>
        <CardDescription>Egyedi nyitás óránként, minden országból együtt, a te időd szerint.</CardDescription>
        <CardAction>
          <Button variant="outline" size="sm" type="button" onClick={onRefresh}>
            <RefreshCwIcon />
            Frissítés
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {points === null ? (
          <p className="text-muted-foreground">
            Az eseménytárház nem adott idősort ehhez a kampányhoz — ezért nincs görbe.
          </p>
        ) : points.length === 0 ? (
          <p className="text-muted-foreground">Még nincs nyitás.</p>
        ) : (
          <Curve data={points} ariaLabel="Egyedi nyitás óránként" actualLabel="nyitás" />
        )}
      </CardContent>
    </Card>
  );
}
